import { useState } from "react";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, Send, Share2, Check, Loader2, Heart, Gift } from "lucide-react";
import { toast } from "sonner";
import { CheckoutButton } from "@/components/CheckoutButton";
import { GiftSteps } from "@/components/GiftSteps";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "short" });

export default function MyList({ open, onOpenChange }) {
  const { profile, wishlist, updateItem, removeItem, refresh } = useProfile();
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = profile ? `${window.location.origin}/gift/${profile.share_token}` : "";

  const sendList = async () => {
    if (wishlist.length === 0) { toast.error("La lista è vuota"); return; }
    setSending(true);
    try {
      await api.post(`/profile/${profile.id}/send-list`);
      toast.success("Lista inviata! Controlla la tua email 🤍");
    } catch {
      toast.error("Invio non riuscito. Riprova.");
    } finally {
      setSending(false);
    }
  };

  const unGift = async (pid) => {
    try {
      await api.delete(`/profile/${profile.id}/gifted/${pid}`);
      toast.success("Segno di regalo rimosso");
      refresh();
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const toGift = wishlist.filter((i) => !i.fully_gifted);
  const done = wishlist.filter((i) => i.fully_gifted);

  const renderItem = (it) => (
                <div key={it.product_id} data-testid={`list-item-${it.product_id}`}
                  className="flex gap-3 rounded-2xl bg-white border border-[#EAE3DC] p-3">
                  {it.image && <img src={it.image} alt={it.title} className="w-20 h-20 rounded-xl object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm font-medium text-[#2D2825] line-clamp-2">{it.title}</div>
                    <div className="text-[#C87D75] font-serif text-lg">{it.price ? `€ ${it.price}` : ""}</div>
                    {it.gifted_qty > 0 && (
                      <div data-testid={`list-gifted-${it.product_id}`} className="mt-1.5 space-y-1">
                        <div className="inline-flex flex-wrap items-center gap-1.5 rounded-full bg-[#EAF0EC] text-[#4E7363] text-[11px] px-2.5 py-0.5">
                          <Gift size={11} /> {it.fully_gifted ? "Regalato" : `Regalati ${it.gifted_qty}/${it.quantity}`}
                          <button data-testid={`list-ungift-${it.product_id}`} onClick={() => unGift(it.product_id)}
                            className="underline ml-1 text-[#8C7F78] hover:text-[#2D2825]">annulla</button>
                        </div>
                        {(it.gifts || []).map((g, gi) => (
                          <div key={`${g.at}-${gi}`} data-testid={`list-gift-detail-${it.product_id}-${gi}`} className="text-[11px] text-[#5C534E] leading-snug">
                            <strong className="text-[#2D2825]">{g.by || "Qualcuno"}</strong>{g.qty > 1 ? ` · x${g.qty}` : ""}{g.at ? ` · ${fmtDate(g.at)}` : ""}
                            {g.message && <span className="block italic text-[#8C7F78]">“{g.message}”</span>}
                          </div>
                        ))}
                      </div>
                    )}
  
                    {it.colors && it.colors.length > 1 && (
                      <select data-testid={`list-color-${it.product_id}`} value={it.color || ""}
                        onChange={(e) => updateItem(it.product_id, { color: e.target.value, color_chosen: true })}
                        className="mt-1.5 rounded-lg bg-[#FAF8F5] border border-[#EAE3DC] text-xs h-8 px-2 max-w-[180px]">
                        {it.colors.map((c) => {
                          const out = it.colors_available && !it.colors_available.includes(c);
                          return <option key={c} value={c} disabled={out}>Colore: {c}{out ? " (esaurito)" : ""}</option>;
                        })}
                      </select>
                    )}
                    {it.colors && it.colors.length === 1 && (
                      <div className="text-xs text-[#8C7F78] mt-1" data-testid={`list-color-${it.product_id}`}>Colore: {it.colors[0]}</div>
                    )}
                    {it.size_unavailable && <div className="text-[11px] text-[#B85750] mt-1" data-testid={`list-size-warning-${it.product_id}`}>Taglia esaurita: scegline un'altra</div>}
                    {it.size_outgrown && (
                      <div data-testid={`list-outgrown-${it.product_id}`} className="mt-1.5 rounded-xl bg-[#FDF6E7] border border-[#F1E2BF] px-2.5 py-2 text-[11px] text-[#7a5f1f]">
                        Questa taglia ora è piccola: {profile?.baby_name || "il tuo bimbo"} porta la <strong>{it.size_now}</strong>.
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {it.size_suggestion ? (
                            <button data-testid={`list-outgrown-switch-${it.product_id}`}
                              onClick={() => { updateItem(it.product_id, { size: it.size_suggestion, size_outgrown: false }); toast.success(`Taglia aggiornata a ${it.size_suggestion}`); }}
                              className="rounded-full bg-[#C87D75] text-white px-3 py-1 text-[11px]">Sostituisci con {it.size_suggestion}</button>
                          ) : (
                            <a data-testid={`list-outgrown-similar-${it.product_id}`} href="/#catalogo" onClick={() => onOpenChange(false)}
                              className="rounded-full bg-[#C87D75] text-white px-3 py-1 text-[11px]">Taglia non disponibile: trova capi simili</a>
                          )}
                          <button data-testid={`list-outgrown-keep-${it.product_id}`} onClick={() => updateItem(it.product_id, { size_outgrown: false, size_ok_for: it.size_now })}
                            className="rounded-full border border-[#E8D6A8] px-3 py-1 text-[11px]">Va bene così</button>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      {it.sizes && it.sizes.length > 0 ? (
                        <select data-testid={`list-size-${it.product_id}`} value={it.size || ""}
                          onChange={(e) => updateItem(it.product_id, { size: e.target.value, size_unavailable: false, size_outgrown: false })}
                          className="rounded-lg bg-[#FAF8F5] border border-[#EAE3DC] text-xs h-8 px-2 min-w-0 flex-1 max-w-[170px]">
                          {it.size_unavailable && <option value={it.size} disabled>{it.size} (esaurita)</option>}
                          {it.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : <span className="text-xs text-[#8C7F78]">Taglia unica</span>}
  
  
                      <div className="flex items-center gap-2 ml-auto shrink-0">
                      <div className="flex items-center rounded-lg border border-[#EAE3DC] bg-white">
                        <button data-testid={`qty-minus-${it.product_id}`}
                          onClick={() => updateItem(it.product_id, { quantity: Math.max(1, it.quantity - 1) })}
                          className="px-2 py-1 text-[#8C7F78]"><Minus size={13} /></button>
                        <span className="text-sm w-6 text-center">{it.quantity}</span>
                        <button data-testid={`qty-plus-${it.product_id}`}
                          onClick={() => updateItem(it.product_id, { quantity: it.quantity + 1 })}
                          className="px-2 py-1 text-[#8C7F78]"><Plus size={13} /></button>
                      </div>
                      <button data-testid={`remove-${it.product_id}`} onClick={() => removeItem(it.product_id)}
                        className="text-[#B85750] hover:opacity-70 ml-1"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </div>
                </div>
    );

  const copyShare = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copiato!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="my-list-panel" className="w-full sm:max-w-md bg-[#FAF8F5] flex flex-col p-0">
        <SheetHeader className="p-6 pb-4 border-b border-[#EAE3DC]">
          <SheetTitle className="font-serif text-2xl text-[#2D2825] flex items-center gap-2">
            <Heart className="text-[#C87D75]" size={20} /> La mia lista nascita
          </SheetTitle>
          <SheetDescription className="text-[#8C7F78]">
            Scegli taglia e quantità, poi inviala allo shop o condividila.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <GiftSteps compact />
          {wishlist.length === 0 ? (
            <div className="text-center text-[#8C7F78] py-10" data-testid="list-empty">
              La tua lista è vuota.<br />Aggiungi i prodotti che ami dal catalogo.
            </div>
          ) : (
            <>
              <div data-testid="list-summary" className="rounded-2xl bg-white border border-[#EAE3DC] px-4 py-3 text-sm text-[#2D2825]">
                <strong>{wishlist.length}</strong> capi · <span className="text-[#4E7363]"><strong>{done.length}</strong> regalati</span> · <span className="text-[#C87D75]"><strong>{toGift.length}</strong> da regalare</span>
              </div>
              {toGift.length > 0 && <div className="pt-2 text-xs uppercase tracking-widest text-[#C87D75]" data-testid="list-group-togift">Da regalare</div>}
              {toGift.map(renderItem)}
              {done.length > 0 && <div className="pt-2 text-xs uppercase tracking-widest text-[#4E7363]" data-testid="list-group-gifted">Già regalati</div>}
              {done.map(renderItem)}
            </>
          )}
        </div>

        <div className="border-t border-[#EAE3DC] p-4 sm:p-6 space-y-2 sm:space-y-3 bg-white pb-safe">
          <CheckoutButton source="list" testId="list-checkout-button" label="Acquista ora con sconto 15%"
            disabled={wishlist.length === 0}
            className="w-full rounded-full bg-[#2D2825] hover:bg-[#473f3a] text-white h-12 text-sm font-medium" />
          <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 sm:gap-3">
          <Button data-testid="send-list-button" onClick={sendList} disabled={sending || wishlist.length === 0}
            className="w-full rounded-full bg-[#E5A8A0] hover:bg-[#C87D75] text-white h-11 sm:h-12 text-xs sm:text-sm px-2">
            {sending ? <Loader2 className="animate-spin" size={18} /> : <><Send size={15} /><span className="hidden sm:inline">Invia la lista allo shop</span><span className="sm:hidden">Invia allo shop</span></>}
          </Button>
          <Button data-testid="share-gift-link-button" onClick={copyShare} disabled={wishlist.length === 0}
            variant="outline" className="w-full rounded-full border-[#A7C4B5] text-[#4E7363] hover:bg-[#EAF0EC] h-11 sm:h-12 text-xs sm:text-sm px-2">
            {copied ? <><Check size={15} className="mr-1.5" /> Copiato</> : <><Share2 size={15} className="mr-1.5 sm:mr-2" /> <span className="sm:hidden">Link regalo</span><span className="hidden sm:inline">Condividi la pagina regalo</span></>}
          </Button>
          </div>
          <p className="text-center text-xs text-[#8C7F78]">
            Al checkout Shopify il tuo sconto del 15% viene applicato in automatico (codice personale, utilizzabile una sola volta, non cumulabile).
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
